import { Component } from '@angular/core';
import { UserService } from './services/user.service';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { LoadingService } from './services/loading.service';
import { Observable, asyncScheduler, combineLatest, firstValueFrom } from 'rxjs';
import { filter, map, observeOn, shareReplay, startWith, distinctUntilChanged, take, timeout } from 'rxjs/operators';
import { NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';
import { environment } from '../environments/environment';
// import { MatDialog } from '@angular/material/dialog';
// import { CartComponent } from './cart/cart.component'; // adjust path if needed
// import { CartService } from './services/cart.service';

@Component({
  selector: 'app-root',
  standalone: false,
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent {

  loading = true;
  readonly httpLoading$: Observable<boolean>;
  readonly globalLoading$: Observable<boolean>;
  title: any;
  private hasShownNetworkAlert = false;
  private hasShownAuthStuckAlert = false;
  private hasClearedStaleMsalInteraction = false;
  private readonly loginRedirectAttemptKey = 'enac.msal.loginRedirect.at';
  // cartCount = 0;

  private getMsalInteractionStatus(): any | null {
    // MSAL stores this under `msal.interaction.status` in temporary cache.
    // Depending on cache settings it may end up in sessionStorage or localStorage.
    const key = 'msal.interaction.status';
    const raw =
      (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(key) : null) ||
      (typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null);
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      return { raw };
    }
  }

  private clearMsalInteractionStatus(): void {
    const key = 'msal.interaction.status';
    try { sessionStorage?.removeItem(key); } catch {}
    try { localStorage?.removeItem(key); } catch {}
  }

  constructor(
    private _user: UserService, 
    private msalService: MsalService, 
    private msalBroadcast: MsalBroadcastService,
    private loadingService: LoadingService,
    private router: Router,
    // private dialog: MatDialog,
    // private cartService: CartService
  ) {
    this.httpLoading$ = this.loadingService.loading$;

    const routeLoading$ = this.router.events.pipe(
      filter(event =>
        event instanceof NavigationStart ||
        event instanceof NavigationEnd ||
        event instanceof NavigationCancel ||
        event instanceof NavigationError
      ),
      map(event => event instanceof NavigationStart),
      startWith(false),
      // Ensure emissions happen asynchronously to avoid NG0100 in dev mode.
      observeOn(asyncScheduler),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.globalLoading$ = combineLatest([this.httpLoading$, routeLoading$]).pipe(
      map(([httpLoading, routeLoading]) => httpLoading || routeLoading),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  ngOnInit() {
    void this.initializeUser();
  }

  // Subscribe to cart count changes
  // this.cartService.cartCount$.subscribe(count => {
  //   this.cartCount = count;
  // });

  // Open cart as right-side panel dialog
  // openCartPanel() {
  //   this.dialog.open(CartComponent, {
  //     width: '840px',
  //     maxWidth: '100%',
  //     height: '100vh',
  //     panelClass: 'right-side-dialog',
  //     hasBackdrop: true,
  //     backdropClass: 'cdk-overlay-dark-backdrop',
  //     position: { right: '0', top: '0' }
  //   });
  // }

  private async initializeUser() {
    try {
      await this.msalService.instance.initialize();

      // Redirect responses are handled by MsalRedirectComponent.
      // Calling handleRedirectPromise() here can race/double-handle and contribute
      // to "login succeeded but no account cached" loops.
      const href = (() => {
        try {
          return window.location.href;
        } catch {
          return '';
        }
      })();
      const looksLikeRedirectResponse = /[?#].*(code=|error=|state=|client_info=)/i.test(href);

      // Wait for MSAL to finish any interaction before deciding whether to login.
      try {
        await firstValueFrom(
          this.msalBroadcast.inProgress$.pipe(
            filter(status => status === InteractionStatus.None),
            take(1),
            timeout({ first: 10000 })
          )
        );
      } catch {
        // Proceed even if we can't observe a stable idle state quickly.
      }
      
      const accounts = this.msalService.instance.getAllAccounts();
      
      if (accounts.length === 0) {
        // Recover from a common failure mode where MSAL's interaction flag is left behind
        // (e.g., browser crashed mid-redirect). In that case MSAL will throw
        // interaction_in_progress and never start a new login.
        if (!looksLikeRedirectResponse && !this.hasClearedStaleMsalInteraction) {
          const status = this.getMsalInteractionStatus();
          const clientId = (status as any)?.clientId;
          if (clientId) {
            this.hasClearedStaleMsalInteraction = true;
            this.clearMsalInteractionStatus();
          }
        }

        // Loop breaker: if we attempted loginRedirect very recently and still have no
        // accounts, do not keep redirecting forever.
        const lastAttemptRaw = (() => {
          try {
            return sessionStorage.getItem(this.loginRedirectAttemptKey);
          } catch {
            return null;
          }
        })();
        const lastAttempt = lastAttemptRaw ? Number(lastAttemptRaw) : 0;
        const now = Date.now();
        if (Number.isFinite(lastAttempt) && lastAttempt > 0 && now - lastAttempt < 30000) {
          this.loading = false;
          if (!this.hasShownAuthStuckAlert) {
            this.hasShownAuthStuckAlert = true;
            window.alert(
              'Sign-in appears to be looping (no account cached after redirect).\n\n' +
                'Try clearing this site\'s storage (Local + Session) and reloading.\n' +
                'If it still loops, the Entra App Registration redirect URI may be misconfigured.'
            );
          }
          return;
        }
        try {
          sessionStorage.setItem(this.loginRedirectAttemptKey, String(now));
        } catch {}

        console.log('No accounts found, redirecting to login...');
        try {
          await this.msalService.loginRedirect({
            // Also request the backend API scope up-front so backend calls don't immediately
            // hit InteractionRequired errors.
            scopes: ['User.Read', 'User.Read.All', environment.msalConfig.backendApiScope]
          });
        } catch (loginError) {
          if ((loginError as any)?.errorCode === 'interaction_in_progress') {
            if (!this.hasShownAuthStuckAlert) {
              this.hasShownAuthStuckAlert = true;
              this.loading = false;
              window.alert(
                'Sign-in is already in progress, but it looks stuck.\n\n' +
                  'Try: hard refresh (Cmd+Shift+R). If it still loops, clear this site\'s local storage/session storage and reload.'
              );
            }
            return;
          }
          throw loginError;
        }
        return;
      }

      // Successful login: clear loop-breaker.
      try { sessionStorage.removeItem(this.loginRedirectAttemptKey); } catch {}
      
      this.msalService.instance.setActiveAccount(accounts[0]);

      // Fallback identity: allow the app to function even if Microsoft Graph is blocked.
      // MSAL accounts typically provide `username` (UPN). GMID is the UPN prefix.
      const activeAccount = this.msalService.instance.getActiveAccount() ?? accounts[0];
      const upn = (activeAccount?.username ?? '').toString();
      const gmid = upn.includes('@') ? upn.split('@')[0].toLowerCase() : upn.toLowerCase();
      if (gmid) {
        this._user.setUserObservable({
          userPrincipalName: upn,
          gmid,
          // keep some helpful display fields if present
          name: (activeAccount as any)?.name,
          tenantId: (activeAccount as any)?.tenantId,
        });
      }

      // Don't block the app on Graph. We'll attempt to enrich the user below.
      this.loading = false;
      
      this._user.graphMeCall().subscribe({
        next: (user: any) => {
          console.log('Graph call returned:', user);
          if (user) {
            user.gmid = user.userPrincipalName.split('@')[0].toLowerCase();
            this._user.setUserObservable(user);
            // Load cart count on page load
            // this.cartService.getCartByGmid(user.gmid).subscribe({
            //   next: (cart: any) => {
            //     console.log('Initial cart loaded for badge count');
            //   },
            //   error: (error) => {
            //     console.error('Error loading initial cart count:', error);
            //   }
            // });
          }
        },
        error: (error) => {
          const isNetworkError = error?.status === 0;
          if (isNetworkError) {
            console.error('Graph call failed due to a network/connectivity issue (status 0).', {
              origin: window.location.origin,
              url: 'https://graph.microsoft.com/v1.0/me',
              online: typeof navigator !== 'undefined' ? navigator.onLine : undefined,
              error
            });

            // Avoid repeated popups if multiple calls fail.
            if (!this.hasShownNetworkAlert) {
              this.hasShownNetworkAlert = true;
              window.alert(
                'Unable to reach Microsoft Graph (network error).\n\n' +
                  'Common causes:\n' +
                  '- VPN/proxy required or disconnected\n' +
                  '- Corporate network blocking microsoftonline.com / graph.microsoft.com\n' +
                  '- You are offline\n\n' +
                  'Check your network/VPN and hard refresh the page.'
              );
            }
          } else {
            console.error('Graph call failed:', error);
          }
          // Keep the fallback user (from MSAL account). Graph is optional.
        }
      });
      
    } catch (error) {
      console.error('Error during initialization:', error);
      this.loading = false;
    }
  }
  
}